'use client';
import Container from '@/app/_components/container';
import { ServiceCard } from '@/app/_components/smallCardsSection/serviceCard';
import { monaFont, plusJakartaSansFont } from '@/styles/fonts';
import { generateServiceLocationUrl } from '@/routes';
import { ServicePathType } from '@/utils/cms/api';
import { capitalizeFirstCharInString, convertSlugToString } from '@/utils/string';
import clsx from 'clsx';
import { MinusIcon, PlusIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/solid';
import parse from 'html-react-parser';
import { ReactNode, useState } from 'react';

const TABS = {
  INFORMAZIONI: 'informazioni',
  SERVIZI: 'servizi',
  RECENSIONI: 'recensioni',
} as const;

type TabValue = (typeof TABS)[keyof typeof TABS];

const HowToBookSection: React.FC<{ title: string; index: number; description: string }> = ({ title, index, description }) => {
  return (
    <div className="flex flex-row items-center gap-3">
      <div className={`flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-darker`}>{index}</div>
      <div className={clsx(plusJakartaSansFont.className, 'flex flex-col gap-0.5')}>
        <h4 className="text-sm font-semibold text-neutral-darker">{title}</h4>
        <p className="text-xs font-medium text-neutral-lighter">{description}</p>
      </div>
    </div>
  );
};

const TabContent: React.FC<{ title: string; children: React.ReactNode; id: TabValue; activeTab: TabValue | null }> = ({
  title,
  children,
  id,
  activeTab,
}) => {
  return (
    <div
      role="tabpanel"
      className={`mb-20 flex flex-col gap-4 rounded-lg border border-neutral-surface bg-white p-4 lg:mx-auto lg:max-w-3xl ${activeTab === id ? 'flex' : 'hidden'}`}
      id={`${id}-panel`}
      aria-labelledby={`${id}-tab`}
    >
      <h1 className="text-base font-semibold">{title}</h1>
      {children}
    </div>
  );
};

export const ProHeader = ({
  serviceName,
  cityName,
  info,
  services,
  reviews,
  title,
  description,
  citySlug,
}: {
  serviceName: string;
  cityName: string;
  description: string;
  citySlug: string;
  title: ReactNode;
  services: { typePath: ServicePathType; description: string; service: string }[];
  info: { question: string; answer: string }[];
  reviews: { name: string; rating: number; description: string }[];
}) => {
  const [activeTab, _setActiveTab] = useState<TabValue | null>(null);

  const setActiveTab = (tab: TabValue) => {
    _setActiveTab(tab === activeTab ? null : tab);
  };

  return (
    <Container>
      <div aria-label={`Informazioni ${serviceName}`} className="flex flex-col  pt-16">
        <div className="mb-10 flex flex-col gap-5 text-primary">
          <h1 className={clsx(monaFont.className, 'mx-auto max-w-5xl text-center text-3xl lg:text-7xl')}>{title}</h1>
          <p className={clsx(plusJakartaSansFont.className, 'text-center text-base !font-semibold lg:text-xl')}>{parse(description)}</p>
        </div>

        {/* Tab Navigation */}
        <div>
          <nav aria-label="Sezioni informative" className="mx-auto mb-2 flex w-full flex-row lg:max-w-lg">
            <button
              role="tab"
              className={`border-primary-light-active flex-1 overflow-hidden rounded-l-lg border-r py-3 ${activeTab === TABS.INFORMAZIONI ? 'bg-primary text-white' : 'bg-primary-light-active'}`}
              aria-selected={activeTab === TABS.INFORMAZIONI}
              aria-controls={`${TABS.INFORMAZIONI}-panel`}
              id={`${TABS.INFORMAZIONI}-tab`}
              onClick={() => setActiveTab(TABS.INFORMAZIONI)}
            >
              Informazioni
            </button>
            {services.length > 0 && (
              <button
                role="tab"
                aria-selected={activeTab === TABS.SERVIZI}
                aria-controls={`${TABS.SERVIZI}-panel`}
                className={`border-primary-light-active flex-1 overflow-hidden border-r py-3 ${activeTab === TABS.SERVIZI ? 'bg-primary text-white' : 'bg-primary-light-active'}`}
                id={`${TABS.SERVIZI}-tab`}
                onClick={() => setActiveTab(TABS.SERVIZI)}
              >
                Servizi
              </button>
            )}
            <button
              role="tab"
              aria-selected={activeTab === TABS.RECENSIONI}
              aria-controls={`${TABS.RECENSIONI}-panel`}
              className={`flex-1 overflow-hidden rounded-r-lg py-3 ${activeTab === TABS.RECENSIONI ? 'bg-primary text-white' : 'bg-primary-light-active'}`}
              id={`${TABS.RECENSIONI}-tab`}
              onClick={() => setActiveTab(TABS.RECENSIONI)}
            >
              Recensioni
            </button>
          </nav>

          <TabContent title={`${serviceName}, tutto quello che devi sapere`} id={TABS.INFORMAZIONI} activeTab={activeTab}>
            <>
              <section aria-labelledby="why-book-heading" className="flex flex-col gap-3 rounded-xl bg-secondary-light-active p-3">
                <h2 id="why-book-heading" className="text-base font-medium">
                  Perché prenotare con Elty?
                </h2>
                <p className="text-sm font-normal text-neutral-darker">
                  Che si tratti di un controllo di routine o di un sintomo che ti preoccupa, in una città dinamica come {cityName} puoi
                  avere accesso a specialisti altamente qualificati, tempi di attesa ridotti e strutture all&apos;avanguardia. Con Elty puoi
                  prenotare {serviceName} a {cityName} in modo semplice e veloce, scegliendo tra i migliori centri della città.
                </p>

                <ul aria-label="Vantaggi del servizio" className="flex flex-col gap-3 lg:grid lg:grid-cols-3">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="size-5 text-secondary" />
                    <span className="text-sm font-medium text-neutral-darker">Confronto prezzi trasparente</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="size-5 text-secondary" />
                    <span className="text-sm font-medium text-neutral-darker">Prenotazione online sicura</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="size-5 text-secondary" />
                    <span className="text-sm font-medium text-neutral-darker">Disponibilità in tempo reale</span>
                  </li>
                </ul>
              </section>

              <section>
                {info.map((item, index) => (
                  <details key={index} className={`group ${index !== info.length - 1 ? 'border-b' : ''} border-b-neutral-200 py-2`}>
                    <summary className="flex cursor-pointer list-none items-center justify-between py-2 text-sm font-semibold">
                      <div>{parse(item.question)}</div>
                      <PlusIcon className="size-5 text-primary-active group-open:hidden" />
                      <MinusIcon className="hidden size-5 text-primary-active group-open:block" />
                    </summary>

                    <div className="text-sm font-normal [&>ol]:list-decimal [&>ul]:list-disc [&_a:hover]:text-primary-hover [&_a]:text-neutral-darker [&_a]:underline [&_a]:transition-colors [&_ol]:pl-8 [&_ul]:pl-8">
                      {parse(item.answer)}
                    </div>
                  </details>
                ))}
              </section>
            </>
          </TabContent>

          {services.length > 0 && (
            <TabContent id={TABS.SERVIZI} activeTab={activeTab} title={`Servizi correlati a ${serviceName}`}>
              <div className="grid grid-cols-2 gap-5 [&>*:nth-child(n+3)]:border-t [&>*:nth-child(n+3)]:border-neutral-200 [&>*:nth-child(n+3)]:pt-4">
                {services.map(it => (
                  <ServiceCard
                    key={it.service}
                    description={it.description}
                    link={generateServiceLocationUrl(it.typePath, { service: it.service, location: citySlug })}
                    service={`${capitalizeFirstCharInString(convertSlugToString(it.service))}`}
                  />
                ))}
              </div>
            </TabContent>
          )}

          <TabContent id={TABS.RECENSIONI} activeTab={activeTab} title={`Recensioni dei pazienti`}>
            <div className="flex items-center gap-3 rounded-xl bg-secondary-light-active p-4 lg:justify-center">
              <span className="text-5xl font-semibold text-secondary">4.7</span>
              <div className="flex flex-col gap-1">
                <div className="flex gap-2 self-start">
                  <StarIcon className="size-6 text-yellow-400" />
                  <StarIcon className="size-6 text-yellow-400" />
                  <StarIcon className="size-6 text-yellow-400" />
                  <StarIcon className="size-6 text-yellow-400" />
                  <div className="relative">
                    <StarIcon className="size-6 text-yellow-400" />
                    <div className="absolute bottom-0 right-0 h-full w-1.5 bg-secondary-light-active"></div>
                  </div>
                </div>
                <span className="text-sm font-normal text-neutral-darker">su 12.426 recensioni verificate</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:grid lg:grid-cols-2 lg:gap-6">
              {reviews.map((it, index) => {
                return (
                  <div key={index} className={`border-b border-neutral-100 pb-2`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <UserCircleIcon className="size-5 text-secondary" />
                        <span className="text-base font-medium text-neutral-darker">{it.name}</span>
                      </div>

                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(key => {
                          return <StarIcon key={key} className={`size-5 ${it.rating >= key ? 'text-yellow-400' : 'text-neutral-100'}`} />;
                        })}
                      </div>
                    </div>
                    <div className="text-sm font-normal text-neutral-lighter">{parse(it.description)}</div>
                  </div>
                );
              })}
            </div>
          </TabContent>
        </div>

        <h4
          className={clsx(plusJakartaSansFont.className, 'mt-16 w-full text-center text-lg font-semibold text-neutral-darker lg:text-2xl')}
        >
          Come prenotare {serviceName} a {cityName}
        </h4>

        <div className="flex flex-col gap-4 py-6 lg:mx-auto lg:grid lg:max-w-5xl lg:grid-cols-3">
          <HowToBookSection
            description={`Visualizza prezzi, disponibilità e recensioni delle migliori cliniche di ${cityName}`}
            title="Confronta"
            index={1}
          />
          <HowToBookSection
            description={`Scegli la data e l'orario più comodi per ${serviceName}`} //
            title="Prenota"
            index={2}
          />

          <HowToBookSection
            description="Ottieni la conferma immediata e recati alla visita con tutte le informazioni"
            title="Presentati"
            index={3}
          />
        </div>
      </div>
    </Container>
  );
};
